import type { FC, ReactElement } from "react"
import { useMemo } from "react"

import { m } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import {
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { EFreeResponsesTheme, FreeResponsesEntity, type TFreeResponsesQuestion } from "@/3514/entities/free-responses"
import { type TAnimatedSectionState, useAnimatedSection } from "@/3514/hooks"
import { EAppAnimationName } from "@/3514/store/app"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { buildCssClassName, withPressHandler } from "@/3514/utils"
import {
    type IUseTranslation,
    type TGetValueFromTranslatedObjectFunction,
    useGetValueFromTranslatedObject,
    useTranslation
} from "@/hooks"

import { type IDiagnosticSummarySummaryModel as ISummaryModel } from "./diagnostic-summary.api.types"
import Module from "./diagnostic-summary.module.component"
import classes from "./diagnostic-summary.styles"
import type {
    TDiagnosticSummaryModalProps as TModalProps,
    TDiagnosticSummaryViewProps as TProps
} from "./diagnostic-summary.ui.types"

const DiagnosticResultsView: FC<TProps> = ({
    data,
    titleText,
    titleAlignment,
    titleSize,
    freeResponsesMapper,
    likertQuestionsMapper,
    entityYGap,
    wrapperClassName = String(""),
    withAnimation = false
}: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    const { shouldAnimate, containerRef, hasPlayed, isMotionReduced }: TAnimatedSectionState = useAnimatedSection({
        animationName: EAppAnimationName.Diagnostic_Summary,
        isDisabled: withAnimation
    })

    const getValueFromTranslatedObjectFunction: TGetValueFromTranslatedObjectFunction =
        useGetValueFromTranslatedObject()

    const freeResponsesQuestions: TFreeResponsesQuestion[] = useMemo(
        (): TFreeResponsesQuestion[] =>
            freeResponsesMapper(data, { t, translator: getValueFromTranslatedObjectFunction }),
        [data, t, getValueFromTranslatedObjectFunction, freeResponsesMapper]
    )

    function handleModalOpen(): void {
        modalDispatcher.showModal<TModalProps>(EModalId.DiagnosticSummary, {
            likertData: data.diagnostics,
            likertQuestionsMapper
        })
    }

    return isEmpty(data) ? null : (
        <m.section
            key="dd"
            ref={containerRef}
            initial={{ opacity: 0, y: 30 }}
            animate={{
                opacity: shouldAnimate || hasPlayed || !withAnimation ? 1 : 0,
                y: shouldAnimate || hasPlayed || !withAnimation ? 0 : 30
            }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: isMotionReduced ? 0 : 0.5, delay: 0.05, ease: "easeIn", type: "tween" }}
            className={buildCssClassName(classes.container(), { [wrapperClassName]: wrapperClassName })}
            style={{ rowGap: `${entityYGap}px` }}
        >
            <Typography
                text={titleText}
                tag={titleSize === ETypographySize.Heading1 ? ETypographyTag.Heading1 : ETypographyTag.Heading2}
                size={titleSize}
                alignment={titleAlignment}
                weight={ETypographyWeight.Medium}
                family={ETypographyFamily.DmSerif}
            />

            <div className={classes.wrapper()} style={{ rowGap: `${entityYGap}px` }}>
                {isEmpty(freeResponsesQuestions) ? (
                    <Typography
                        text="No answers recorded."
                        size={ETypographySize.Small}
                        color={ETypographyColor.Dark}
                        className={classes.modules.emptyMessages()}
                    />
                ) : (
                    <FreeResponsesEntity
                        theme={EFreeResponsesTheme.Transparent}
                        questions={freeResponsesQuestions}
                        title={null}
                        isRequired={false}
                        isError={false}
                    />
                )}

                <div className={classes.modules.container()} hidden={isEmpty(data.summaries)}>
                    <div className={classes.modules.wrapper()}>
                        {map<ISummaryModel, ReactElement>(
                            data.summaries,
                            (s: ISummaryModel): ReactElement => (
                                <Module key={`m-c-${s.category.id}`} summary={s} />
                            )
                        )}
                    </div>

                    <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={withPressHandler(handleModalOpen)}
                        onClick={withPressHandler(handleModalOpen)}
                        className={classes.modal.opener()}
                    >
                        <Typography
                            text={t("entities.diagnosticSummary.modal.opener")}
                            size={ETypographySize.Small}
                            color={ETypographyColor.Blue200}
                            className={classes.modal.openerText()}
                        />
                    </div>
                </div>
            </div>
        </m.section>
    )
}

DiagnosticResultsView.displayName = "DiagnosticResultsView"

export default DiagnosticResultsView
